import React, { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FaRegStickyNote } from "react-icons/fa";
import "../styles/PendentNotesModal.css";

/**
 * PendentNotesModal
 * Props:
 * - isOpen: boolean
 * - pendentLoad: { id: number|string, uiCounter?: number|string } | null
 * - defaultValue: string
 * - onSave: (text: string) => void
 * - onClose: () => void
 */
export default function PendentNotesModal({
  isOpen,
  pendentLoad,
  defaultValue = "",
  onSave,
  onClose,
}) {
  const { t } = useTranslation("pendent_notes_modal");
  const [draft, setDraft] = useState(defaultValue);
  const textRef = useRef(null);

  const MAX_LEN = 500;

  useEffect(() => {
    setDraft(defaultValue || "");
  }, [defaultValue, isOpen, pendentLoad?.id]);

  useEffect(() => {
    if (isOpen && textRef.current) {
      textRef.current.focus();
      const el = textRef.current;
      const v = el.value;
      el.value = "";
      el.value = v;
    }
  }, [isOpen]);

  const handleOverlayClick = useCallback(() => onClose(), [onClose]);
  const stop = (e) => e.stopPropagation();

  const handleSave = useCallback(() => {
    onSave((draft || "").trim());
  }, [draft, onSave]);

  const clearText = useCallback(() => {
    setDraft("");
    requestAnimationFrame(() => textRef.current?.focus());
  }, []);

  const insertTemplate = useCallback(() => {
    const base = t("template.base", {
      defaultValue: "• Pick-up:\n• Delivery:\n• Extra note:",
    });

    setDraft((prev) => (prev?.trim() ? `${prev.trim()}\n${base}` : base));
    requestAnimationFrame(() => textRef.current?.focus());
  }, [t]);

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      onClose();
      return;
    }

    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "enter") {
      handleSave();
      return;
    }

    if (e.key === "Tab") {
      const modal = e.currentTarget.querySelector(".pendent-notes-modal");
      if (!modal) return;

      const focusables = modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      const list = Array.from(focusables).filter(
        (el) => !el.hasAttribute("disabled")
      );

      if (list.length === 0) return;

      const first = list[0];
      const last = list[list.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        last.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }
  };

  if (!isOpen) return null;

  const title = pendentLoad?.uiCounter
    ? t("title.withNumber", {
        number: pendentLoad.uiCounter,
        defaultValue: `Notes for pendent load #${pendentLoad.uiCounter}`,
      })
    : t("title.default", {
        defaultValue: "Pendent load notes",
      });

  const subtitle = t("subtitle", {
    defaultValue: "Add internal notes for this pendent load.",
  });

  const chars = draft.length;

  return (
    <div
      className="pendent-notes-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pendent-notes-modal-title"
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
    >
      <div
        className="pendent-notes-modal"
        onClick={stop}
        role="document"
        aria-labelledby="pendent-notes-modal-title"
      >
        <div className="pendent-notes-modal-header">
          <div className="pendent-notes-modal-icon" aria-hidden="true">
            <FaRegStickyNote />
          </div>

          <div className="pendent-notes-modal-titlewrap">
            <div
              className="pendent-notes-modal-title"
              id="pendent-notes-modal-title"
            >
              {title}
            </div>
            <div className="pendent-notes-modal-subtitle">{subtitle}</div>
          </div>

          <button
            className="pendent-notes-modal-close"
            type="button"
            aria-label={t("aria.close", { defaultValue: "Close" })}
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div className="pendent-notes-modal-body">
          <div className="pendent-notes-field">
            <div className="pendent-notes-label-row">
              <span>
                {t("labels.writeNotes", { defaultValue: "Write notes" })}
              </span>
            </div>

            <div className="pendent-notes-textarea-wrap">
              <textarea
                ref={textRef}
                id="pendent-notes-textarea"
                className="pendent-notes-textarea"
                value={draft}
                onChange={(e) => setDraft(e.target.value.slice(0, MAX_LEN))}
                placeholder={t("placeholder", {
                  defaultValue:
                    "Example: client asked to confirm unloading slot before arrival.",
                })}
                aria-label={t("aria.textarea", {
                  defaultValue: "Notes text area",
                })}
                maxLength={MAX_LEN}
              />

              <div
                id="pendent-notes-charcount"
                className="pendent-notes-charcount"
                aria-live="polite"
              >
                {t("counter", {
                  current: chars,
                  max: MAX_LEN,
                  defaultValue: `${chars}/${MAX_LEN}`,
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="pendent-notes-modal-footer">
          <div className="pendent-notes-footer-left">
            <button
              className="pendent-notes-btn pendent-notes-btn--ghost"
              onClick={clearText}
              type="button"
            >
              {t("buttons.clear", { defaultValue: "Clear" })}
            </button>

            <button
              className="pendent-notes-btn pendent-notes-btn--ghost"
              onClick={insertTemplate}
              type="button"
            >
              {t("buttons.insertTemplate", {
                defaultValue: "Insert template",
              })}
            </button>
          </div>

          <div className="pendent-notes-footer-right">
            <button
              className="pendent-notes-btn pendent-notes-btn--ghost"
              onClick={onClose}
              type="button"
            >
              {t("buttons.close", {
                defaultValue: "Close",
              })}
            </button>

            <button
              className="pendent-notes-btn pendent-notes-btn--primary"
              onClick={handleSave}
              type="button"
            >
              {t("buttons.save", { defaultValue: "Save" })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}